import React from 'react';
import { Div, P } from '@kizi/core-components';
import { IconV2, withTooltip } from '@kizi/shared-components';
import { isMobile } from '../../utils/global';

const IconWithTooltip = withTooltip(IconV2);

interface TopThreeBadgeProps {
  tag: string;
  type: string;
}

const handleBadgeLayout = ({ tag, type }) => {
  if (type === 'tripleplay') {
    const tagValue = tag[0];

    const textStyles = {
      color: '#05164C',
      fontSize: '14px',
      fontWeight: 400,
      padding: '4px 8px',
    };

    switch (tagValue) {
      case 'highestRating':
        return {
          css: {
            background: '#BB8CE1',
            '>p': textStyles,
            'svg': {
              fill: '#05164C',
            },
          },
          label: 'Hoogste testoordeel',
          tooltip:
            'Deze score is gebaseerd op de ervaringen van het Consumentenbond Panel en voldoet aan jouw voorkeuren.',
        };
      case 'fastestSpeed':
        return {
          css: {
            background: '#F6CA4A',
            '>p': textStyles,
            'svg': {
              fill: '#05164C',
            },
          },
          label: 'Snelste',
          tooltip:
            'Dit is het abonnement met de hoogste snelheid op basis van jouw voorkeuren.',
        };
      case 'lowestPrice':
        return {
          css: {
            background: '#8AD35D',
            '>p': textStyles,
            'svg': {
              fill: '#05164C',
            },
          },
          label: 'Goedkoopste',
          tooltip:
            'Dit is het goedkoopste abonnement dat voldoet aan jouw voorkeuren.',
        };

      default:
        break;
    }
  }

  switch (tag) {
    case 'highestScore': {
      return {
        css: {
          background: '#D9E8FC',
          '>p': {
            color: '#0B428E',
            fontSize: '14px',
            fontWeight: 700,
            padding: '4px 8px',
          },
          'svg': {
            fill: '#0B428E',
          },
        },
        label: 'Beste product',
        tooltip:
          'Dit is de zorgverzekering die voldoet aan jouw voorkeuren en het beste scoort in onze productbeoordeling.',
      };
    }
    // third group
    case 'sustainableChoice': {
      return {
        css: {
          background: '#FFEEB8',
          '>p': {
            color: '#705600',
            fontSize: '14px',
            fontWeight: 700,
            padding: '4px 8px',
          },
          'svg': {
            fill: '#705600',
          },
        },
        label: 'Duurzame keuze',
        tooltip:
          'De verzekeraar van dit product heeft de hoogste score op duurzaamheid.',
      };
    }
    case 'healthyLiving': {
      return {
        css: {
          background: '#FFEEB8',
          '>p': {
            color: '#705600',
            fontSize: '14px',
            fontWeight: 700,
            padding: '4px 8px',
          },
          'svg': {
            fill: '#705600',
          },
        },
        label: 'Vitale leefstijl',
        tooltip:
          'Dit product heeft de hoogste score op aanvullende diensten voor een vitale leefstijl.',
      };
    }
    case 'highestScoreForPrice': {
      return {
        css: {
          background: '#FFEEB8',
          '>p': {
            color: '#705600',
            fontSize: '14px',
            fontWeight: 700,
            padding: '4px 8px',
          },
          'svg': {
            fill: '#705600',
          },
        },
        label: 'Beste prijs-kwaliteit',
        tooltip:
          'Dit product heeft de beste verhouding tussen prijs en kwaliteit.',
      };
    }
    case 'lowestPrice': {
      return {
        css: {
          background: '#C2FAE7',
          '>p': {
            color: '#09714F',
            fontSize: '14px',
            fontWeight: 700,
            padding: '4px 8px',
          },

          'svg': {
            fill: '#09714F',
          },
        },
        label: 'Goedkoopste',
        tooltip:
          type === 'os-triple-play'
            ? 'Dit is het voordeligste abonnement dat voldoet aan jouw voorkeuren.'
            : 'Dit product voldoet aan jouw voorkeuren en is het voordeligst.',
      };
    }
    case 'moreHospitalChoice': {
      return {
        css: {
          background: '#D9E8FC',
          '>p': {
            color: '#0B428E',
            fontSize: '14px',
            fontWeight: 700,
            padding: '4px 8px',
          },
          'svg': {
            fill: '#0B428E',
          },
        },
        label: 'Ruimere ziekenhuis keuze',
        tooltip:
          'Dit product heeft een ruimere ziekenhuiskeuze dan het goedkoopste product.',
      };
    }
    case 'completeHospitalChoice': {
      return {
        css: {
          background: '#D9E8FC',
          '>p': {
            color: '#0B428E',
            fontSize: '14px',
            fontWeight: 700,
            padding: '4px 8px',
          },
          'svg': {
            fill: '#0B428E',
          },
        },
        label: 'Volledige ziekenhuis keuze',
        tooltip: 'Dit product heeft een volledige ziekenhuiskeuze.',
      };
    }
    case 'fastestSpeed': {
      return {
        css: {
          background: '#FFEEB8',
          '>p': {
            color: '#705600',
            fontSize: '14px',
            fontWeight: 700,
            padding: '4px 8px',
          },
          'svg': {
            fill: '#705600',
          },
        },
        label: 'Snelste',
        tooltip:
          'Dit is het snelste abonnement dat voldoet aan jouw voorkeuren.',
      };
    }
    case 'featured': {
      return {
        css: {
          background: '#D9E8FC',
          '>p': {
            color: '#0B428E',
            fontSize: '14px',
            fontWeight: 700,
            padding: '4px 8px',
          },
          'svg': {
            fill: '#0B428E',
          },
        },
        label: 'Uitgelicht',
        tooltip:
          'Dit is een aanbevolen abonnement dat voldoet aan jouw voorkeuren.',
      };
    }
    default: {
      return {
        css: {
          background: 'gray',
        },
        label: 'Unknown Type',
        tooltip: 'No tooltip available for this type',
      };
    }
  }
};

export const TopThreeBadge: React.FC<TopThreeBadgeProps> = ({
  tag = 'best',
  type,
}) => {
  const { css, label, tooltip } = handleBadgeLayout({ tag, type });

  return (
    <Div
      css={{
        width: 'fit-content',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: '200px',
        padding: '0 8px',
        position: 'absolute',
        top: '-15px',
        left: '5px',
        ...css,
      }}
    >
      <P variant="footnoteRegular">{label} </P>
      {tooltip && (
        <IconWithTooltip
          name="info"
          size={14}
          _popTo={isMobile() ? 'right' : 'top'}
          css={{ fill: '#D9DADE', marginRight: '0!important' }}
          _tooltip={tooltip}
          cursor="pointer"
        />
      )}
    </Div>
  );
};
